/**
 * Hook to fetch current SELIC meta rate from Banco Central (SGS série 432).
 *
 * - Caches last successful value in localStorage for 24h
 * - Falls back to a sensible default if the API fails / is offline
 * - Returns annual rate (%) and derived daily rate (pro-rated 252 business days)
 */

import { useEffect, useState } from 'react'

const SGS_SELIC_META_URL = 'https://api.bcb.gov.br/dados/serie/bcdata.sgs.432/dados/ultimos/1?formato=json'
const CACHE_KEY = 'monutrack:selic-rate'
const CACHE_TTL_MS = 24 * 60 * 60 * 1000 // 24h
const FALLBACK_ANNUAL_RATE = 11.0 // % a.a. — usado se a API falhar

export interface SelicRate {
  annual: number      // taxa anual (%) ex: 10.75
  daily: number       // taxa diária (%) ex: 0.0427
  source: 'bcb' | 'cache' | 'fallback'
  fetchedAt: string   // ISO date
}

interface CachedPayload {
  annual: number
  fetchedAt: string
}

function readCache(): CachedPayload | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as CachedPayload
    const age = Date.now() - new Date(parsed.fetchedAt).getTime()
    if (age > CACHE_TTL_MS) return null
    return parsed
  } catch {
    return null
  }
}

function writeCache(annual: number): void {
  try {
    const payload: CachedPayload = { annual, fetchedAt: new Date().toISOString() }
    localStorage.setItem(CACHE_KEY, JSON.stringify(payload))
  } catch {
    /* ignore quota errors */
  }
}

/** Convert annual rate (% a.a.) into daily rate (% a.d.) using 252 business days. */
function toDailyRate(annual: number): number {
  return (Math.pow(1 + annual / 100, 1 / 252) - 1) * 100
}

export function useSelicRate(): SelicRate {
  // initial state: try cache, else fallback
  const [rate, setRate] = useState<SelicRate>(() => {
    const cached = readCache()
    if (cached) {
      return {
        annual: cached.annual,
        daily: toDailyRate(cached.annual),
        source: 'cache',
        fetchedAt: cached.fetchedAt,
      }
    }
    return {
      annual: FALLBACK_ANNUAL_RATE,
      daily: toDailyRate(FALLBACK_ANNUAL_RATE),
      source: 'fallback',
      fetchedAt: new Date().toISOString(),
    }
  })

  useEffect(() => {
    // Skip fetch if cache is fresh
    if (readCache()) return

    let cancelled = false
    const controller = new AbortController()

    fetch(SGS_SELIC_META_URL, { signal: controller.signal })
      .then((res) => {
        if (!res.ok) throw new Error(`BCB SGS responded ${res.status}`)
        return res.json()
      })
      .then((data: Array<{ valor: string }>) => {
        if (cancelled) return
        const latest = data[data.length - 1]
        const annual = parseFloat(latest?.valor ?? '')
        if (!isFinite(annual) || annual <= 0) throw new Error('invalid BCB payload')
        writeCache(annual)
        setRate({
          annual,
          daily: toDailyRate(annual),
          source: 'bcb',
          fetchedAt: new Date().toISOString(),
        })
      })
      .catch(() => {
        /* stays on fallback/cache */
      })

    return () => {
      cancelled = true
      controller.abort()
    }
  }, [])

  return rate
}
